/**
 * A case's dashboard, read from `GET /v1/cases/{id}/dashboard` (PRD-0075 R-D2), as of now or as of
 * an instant (*Spis k dátumu*, R-D4).
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { CaseDashboardModel } from '@/components/case-dashboard/dashboard-view'
import { caseDashboardApi } from '@/services/api/case-dashboard'
import { ApiError } from '@/services/api/client'
import { toDashboardModel } from './dashboard-model'

export const caseDashboardKeys = {
  dashboard: (caseId: string) => ['case', caseId, 'dashboard'] as const,
  /** Under the dashboard's key: refreshing the dashboard refreshes every date it was read as of. */
  asOf: (caseId: string, asOf: string | undefined) =>
    [...caseDashboardKeys.dashboard(caseId), asOf ?? 'now'] as const,
}

/** A refusal (not a member, unknown case) answers the same again; an outage may not. */
function retryUnlessRefused(failureCount: number, error: unknown): boolean {
  const refused = error instanceof ApiError && error.status >= 400 && error.status < 500
  return !refused && failureCount < 2
}

export function useCaseDashboard(caseId: string, asOf?: string) {
  return useQuery<CaseDashboardModel>({
    queryKey: caseDashboardKeys.asOf(caseId, asOf),
    queryFn: async ({ signal }) =>
      toDashboardModel(await caseDashboardApi.get(caseId, { asOf, signal })),
    // Another date keeps the dashboard on screen until the file as of it arrives.
    placeholderData: keepPreviousData,
    enabled: Boolean(caseId),
    retry: retryUnlessRefused,
  })
}
